import { forwardRef } from "react";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { Button, Dropdown } from "semantic-ui-react";

import "components/Calendar/Calendar.scss";
import { DatePickerContainer } from "modules/WarrantyDashboard/components/WarrantyDashboardFilters/components";
import { usePinPeriodDropdown } from "modules/WarrantyDashboard/components/WarrantyDashboardFilters/components/PinPeriodDropdown/hooks";
import "modules/WarrantyDashboard/components/WarrantyDashboardFilters/components/PinPeriodDropdown/PinPeriodDropdown.scss";
import { WARRANTY_TIME_PERIODS, useGetTimePeriodOptions } from "modules/WarrantyDashboard/hooks";
import { getFormattedDateRange } from "utils/dateHelperFunctions";

const CustomDatePickerInput = forwardRef<HTMLButtonElement>(() => null);
CustomDatePickerInput.displayName = "CustomDatePickerInput";

type PinPeriodDropdownProps = {
  value: WARRANTY_TIME_PERIODS;
  handleDateChange: (dates: [Date | null, Date | null]) => void;
  handleTimePeriodChange: (value: WARRANTY_TIME_PERIODS) => void;
};

export const PinPeriodDropdown = ({ handleTimePeriodChange, value, handleDateChange }: PinPeriodDropdownProps) => {
  const { t } = useTranslation();

  const timePeriodOptions = useGetTimePeriodOptions();
  const {
    show,
    endDate,
    startDate,
    datePickerRef,
    currentTimePeriodLabel,
    updatedTimePeriodOptions,
    onDateChange,
    handleResetDateRange,
    handleOpenDatePicker,
    handleCloseDatePicker
  } = usePinPeriodDropdown({ handleDateChange, selectedWarrantyTimePeriod: value });

  return (
    <div className="PinPeriodDropdown">
      <Dropdown selection fluid value={value} placeholder={t("v8_time_period", "Time period")} options={timePeriodOptions} text={currentTimePeriodLabel}>
        <Dropdown.Menu>
          {updatedTimePeriodOptions.map(option => (
            <Dropdown.Item
              {...option}
              key={option.key}
              selected={value === option.value}
              className={`${value === option.value ? "bold-text" : ""}`}
              onClick={(_, { value }) => {
                handleTimePeriodChange(value as WARRANTY_TIME_PERIODS);
                option.value === WARRANTY_TIME_PERIODS.CustomTimePeriod && handleOpenDatePicker();
              }}
            >
              <p className="ellipsis">
                {option.text}{" "}
                {value === WARRANTY_TIME_PERIODS.CustomTimePeriod && option.value === WARRANTY_TIME_PERIODS.CustomTimePeriod && getFormattedDateRange(startDate, endDate)}
              </p>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      <div className="no-size">
        <ReactDatePicker
          calendarClassName="CalendarDatepicker"
          selectsRange
          open={show}
          startDate={startDate}
          endDate={endDate}
          ref={datePickerRef}
          showPopperArrow={false}
          selected={endDate || new Date()}
          customInput={<CustomDatePickerInput />}
          onClickOutside={handleCloseDatePicker}
          onChange={dates => onDateChange(dates)}
          calendarContainer={DatePickerContainer}
        >
          <div className="react-datepicker-clear-date-container">
            <Button
              content={t("v8_clear_date_range", "Clear date range")}
              icon="calendar"
              labelPosition="left"
              onClick={handleResetDateRange}
              className="react-datepicker-clear-date-button"
            />
          </div>
        </ReactDatePicker>
      </div>
    </div>
  );
};
